import { useState } from "react";
import { Box, Flex, Icon, Tag, TagLabel, Text, Tooltip } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  MdAlternateEmail,
  MdBadge,
  MdBusiness,
  MdCalendarToday,
  MdNumbers,
  MdPhone,
  MdShortText,
} from "react-icons/md";
import {
  ContextoDisparo,
  PropriedadeCatalogo,
  RelacaoCatalogo,
  TipoPropriedade,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import { GRUPO_RAIZ, grupoDaPropriedade as grupoDa, rotuloDoGrupo } from "./catalogo.helpers";
import { brandOutline, brandSolid, fieldFont } from "./styleTokens";

type Props = {
  propriedades: PropriedadeCatalogo[];
  onSelect: (propriedade: PropriedadeCatalogo) => void;
  /** Texto mostrado quando a lista está vazia. */
  vazio?: string;
  /** Ids das propriedades já em uso — a pill aparece preenchida. */
  usadas?: string[];
  /** Nomeia o grupo das propriedades sem relação: "Contexto atual (Proposta)". */
  contexto?: ContextoDisparo;
  /** Nome do contexto vindo do catálogo — o enum só nomeia três. */
  nomeContexto?: string;
  /** Relações do contexto — de onde vem o nome de negócio de cada grupo. */
  relacoes?: RelacaoCatalogo[];
  /**
   * Grupos recolhidos. Opcional: sem ele a lista guarda o estado sozinha. A
   * sidebar controla de fora porque desmonta a lista quando o foco troca de
   * campo, e o que foi recolhido não deve voltar a abrir.
   */
  recolhidos?: string[];
  onRecolhidosChange?: (recolhidos: string[]) => void;
  /** Ignora o recolhido — usado enquanto há termo de busca. */
  forcarAberto?: boolean;
  /** Grupo escolhido na fileira "Entidade": vem primeiro, sem esconder os outros. */
  destaque?: string | null;
};

// Ícone por tipo, no espírito do QueryColumnPicker do Metabase (cada coluna
// mostra um ícone conforme o tipo) — ajuda a escanear a lista. Exportado
// pra reaproveitar no popover de operador/valor (FiltroCondicaoCard).
export const TIPO_ICON: Record<TipoPropriedade, typeof MdShortText> = {
  [TipoPropriedade.TEXTO]: MdShortText,
  [TipoPropriedade.NUMERO]: MdNumbers,
  [TipoPropriedade.DATA]: MdCalendarToday,
  [TipoPropriedade.CPF]: MdBadge,
  [TipoPropriedade.CNPJ]: MdBusiness,
  [TipoPropriedade.EMAIL]: MdAlternateEmail,
  [TipoPropriedade.TELEFONE]: MdPhone,
};

/**
 * As propriedades do catálogo vêm achatadas (`tomador.nome`, `status`). O
 * prefixo antes do ponto é a relação declarada pelo backend — usamos ele
 * para agrupar visualmente, sem inventar entidade nenhuma. Propriedade sem
 * ponto é do próprio contexto.
 */
// Regra de agrupamento e rótulos ficam no catalogo.helpers, junto do resto
// da leitura do catálogo — a sidebar usa os mesmos para montar o seletor de
// entidade.

function PropriedadePill({
  label,
  tipo,
  hint,
  usada,
  onClick,
}: {
  label: string;
  tipo: TipoPropriedade;
  hint?: string;
  usada?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip label={hint} placement="top" openDelay={300} isDisabled={!hint}>
      <Tag
        // preventDefault no mousedown evita que o clique tire o foco do
        // campo de mensagem antes do onClick disparar.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        size="md"
        borderRadius="full"
        cursor="pointer"
        // Preenchida = já virou variável na mensagem. Mesma borda nos dois
        // estados, senão a pill muda de tamanho ao ser usada.
        {...(usada
          ? { ...brandSolid("secondary"), border: "2px solid", borderColor: "secondary" }
          : brandOutline("secondary"))}
        {...fieldFont}
      >
        <Icon as={TIPO_ICON[tipo]} boxSize={3} mr={1.5} />
        <TagLabel>{label}</TagLabel>
      </Tag>
    </Tooltip>
  );
}

// Lista de propriedades do contexto, agrupada pela relação do catálogo — o
// "column picker" do Metabase (FilterPicker → FilterColumnPicker), adaptado
// ao catálogo do backend e ao restante da sidebar: cada propriedade é uma
// pill (mesmo widget das outras listas), não uma linha.
export default function PropriedadeList({
  propriedades,
  onSelect,
  vazio,
  usadas,
  contexto,
  nomeContexto,
  relacoes,
  recolhidos: recolhidosControlado,
  onRecolhidosChange,
  forcarAberto,
  destaque,
}: Props) {
  const [recolhidosLocal, setRecolhidosLocal] = useState<string[]>([]);
  const recolhidos = recolhidosControlado ?? recolhidosLocal;

  const grupos = propriedades.reduce<Record<string, PropriedadeCatalogo[]>>((acc, p) => {
    const grupo = grupoDa(p.id);
    (acc[grupo] ??= []).push(p);
    return acc;
  }, {});

  const peso = (g: string) => (g === destaque ? 0 : g === GRUPO_RAIZ ? 1 : 2);
  const ordem = Object.keys(grupos).sort((a, b) => peso(a) - peso(b) || a.localeCompare(b));

  if (propriedades.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400" px={1} py={2} {...fieldFont}>
        {vazio ?? "Nada encontrado."}
      </Text>
    );
  }

  const rotuloRaiz = contexto
    ? `Contexto atual (${nomeContexto ?? rotuloContexto(contexto)})`
    : "Contexto atual";

  // Recolhido vale só fora da busca: com termo digitado, esconder o grupo
  // esconderia justamente o resultado procurado.
  const aberto = (grupo: string) => forcarAberto || !recolhidos.includes(grupo);

  const alternar = (grupo: string) => {
    const proximo = recolhidos.includes(grupo)
      ? recolhidos.filter((g) => g !== grupo)
      : [...recolhidos, grupo];
    if (onRecolhidosChange) onRecolhidosChange(proximo);
    else setRecolhidosLocal(proximo);
  };

  return (
    <Box>
      {ordem.map((grupo) => {
        const emUso = grupos[grupo].filter((p) => usadas?.includes(p.id)).length;
        return (
          <Box key={grupo} mb={2}>
            <Flex
              as="button"
              type="button"
              // Mesmo motivo das pills: não tirar o foco do campo de mensagem.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => alternar(grupo)}
              align="center"
              gap={1}
              w="100%"
              px={1}
              pb={1}
              color="gray.500"
              _hover={{ color: "gray.700" }}
              aria-expanded={aberto(grupo)}
            >
              <Icon as={aberto(grupo) ? ChevronDownIcon : ChevronRightIcon} boxSize={3.5} />
              <Text
                fontSize="10px"
                fontWeight="700"
                letterSpacing="0.04em"
                textTransform="uppercase"
                textAlign="left"
                {...fieldFont}
              >
                {rotuloDoGrupo(grupo, rotuloRaiz, relacoes)} ({grupos[grupo].length})
                {/* Recolhido, as pills preenchidas somem — o número avisa. */}
                {!aberto(grupo) && emUso > 0 && ` · ${emUso} em uso`}
              </Text>
            </Flex>
            {aberto(grupo) && (
              <Flex align="center" gap={2} wrap="wrap">
                {grupos[grupo].map((p) => (
                  <PropriedadePill
                    key={p.id}
                    label={p.descricao}
                    tipo={p.tipo}
                    hint={p.id}
                    usada={usadas?.includes(p.id)}
                    onClick={() => onSelect(p)}
                  />
                ))}
              </Flex>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
